import React, { useState, useEffect } from 'react';
import { adminService, clubService } from '../services/apiClient';
import Navbar from '../components/Navbar';
import { Users, Shield, Layout, Activity, ChevronRight, UserPlus } from 'lucide-react';

const SuperAdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('users');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, userRes, clubRes] = await Promise.all([
                adminService.getAnalytics(),
                adminService.getAllUsers(),
                clubService.getAllClubs()
            ]);
            setStats(statsRes.data);
            setUsers(userRes.data);
            setClubs(clubRes.data);
        } catch (error) {
            console.error('Failed to fetch super admin data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await adminService.updateUserRole({ userId, role: newRole });
            fetchData();
        } catch (error) {
            alert('Failed to update role');
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Super Admin Panel...</div>;

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 py-8">
                <header className="mb-10 flex justify-between items-end">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Platform Controls</h1>
                        <p className="text-gray-500 mt-2">Manage users, clubs, and platform-wide configurations.</p>
                    </div>
                    <div className="flex gap-4">
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                                <Activity className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-gray-400 uppercase">System Status</p>
                                <p className="text-sm font-bold text-gray-900 text-green-600">Operational</p>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    {[
                        { label: 'Total Students', value: stats?.totalStudents, icon: <Users />, color: 'blue' },
                        { label: 'Active Clubs', value: stats?.totalClubs, icon: <Layout />, color: 'purple' },
                        { label: 'Total Events', value: stats?.totalEvents, icon: <Activity />, color: 'orange' },
                        { label: 'Active Users', value: stats?.activeUsers, icon: <Shield />, color: 'green' }
                    ].map((stat, i) => (
                        <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition">
                            <div className={`w-12 h-12 bg-${stat.color}-100 text-${stat.color}-600 rounded-xl flex items-center justify-center mb-4`}>
                                {React.cloneElement(stat.icon, { className: 'w-6 h-6' })}
                            </div>
                            <p className="text-gray-500 text-sm font-medium">{stat.label}</p>
                            <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                        </div>
                    ))}
                </div>

                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="flex border-b border-gray-100">
                        {['users', 'clubs'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-8 py-5 font-bold text-sm uppercase tracking-wider transition relative ${activeTab === tab ? 'text-purple-600' : 'text-gray-400 hover:text-gray-600'
                                    }`}
                            >
                                {tab}
                                {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-1 bg-purple-600"></div>}
                            </button>
                        ))}
                    </div>

                    <div className="p-8">
                        {activeTab === 'users' ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="text-gray-400 text-xs font-bold uppercase tracking-widest border-b border-gray-50">
                                            <th className="pb-4">User</th>
                                            <th className="pb-4">Role</th>
                                            <th className="pb-4">Joined</th>
                                            <th className="pb-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {users.map(u => (
                                            <tr key={u._id} className="hover:bg-gray-50 transition group">
                                                <td className="py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
                                                            {u.name.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-gray-900">{u.name}</p>
                                                            <p className="text-xs text-gray-500">{u.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-4">
                                                    <span className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest ${u.role === 'SuperAdmin' ? 'bg-red-100 text-red-600' :
                                                            u.role === 'ClubAdmin' ? 'bg-blue-100 text-blue-600' :
                                                                'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {u.role}
                                                    </span>
                                                </td>
                                                <td className="py-4 text-sm text-gray-500">
                                                    {new Date(u.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-4 text-right">
                                                    <select
                                                        onChange={(e) => handleRoleChange(u._id, e.target.value)}
                                                        value={u.role}
                                                        className="bg-gray-50 border border-gray-200 text-xs font-bold p-2 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                                                    >
                                                        <option value="Student">Student</option>
                                                        <option value="ClubAdmin">Club Admin</option>
                                                        <option value="SuperAdmin">Super Admin</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {clubs.map(c => (
                                    <div key={c._id} className="group border border-gray-100 rounded-2xl p-6 hover:shadow-lg transition bg-white flex flex-col justify-between">
                                        <div>
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-xl font-bold text-indigo-600">
                                                    {c.name.charAt(0)}
                                                </div>
                                                <span className="text-[10px] font-extrabold uppercase bg-gray-50 px-2 py-1 rounded-md text-gray-400">
                                                    {c.category}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 mb-2">{c.name}</h3>
                                            <p className="text-sm text-gray-500 mb-6 line-clamp-2">{c.description}</p>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="bg-gray-50 p-3 rounded-xl">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Current Admin</p>
                                                <p className="text-sm font-bold text-gray-800">{c.admin?.name || 'Unassigned'}</p>
                                            </div>
                                            <button
                                                onClick={() => {/* Open Assign Admin Modal */ }}
                                                className="w-full py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition flex items-center justify-center gap-2"
                                            >
                                                <UserPlus className="w-4 h-4" />
                                                Assign New Admin
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
